import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  CheckCircle, 
  AlertTriangle, 
  Clock,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

interface SlotResult {
  slotId: number;
  title: string;
  rawScore: string | null;
  maxScore: string;
  percentage: number | null;
}

interface CategoryResult {
  categoryId: number;
  categoryName: string;
  weightPercent: string;
  average: number | null;
  slots: SlotResult[];
}

interface CourseSummary {
  planId: number;
  planTitle: string;
  periodName: string;
  courseName: string;
  categories: CategoryResult[];
  finalAverage: number | null;
  riskLevel: string;
}

interface GradesData {
  student: {
    id: number;
    nui: string;
    fullName: string;
    groupId: number;
  };
  summaries: CourseSummary[];
}

export default function AlumnoGrades() {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery<GradesData>({
    queryKey: ['student-grades-summary'],
    queryFn: async () => {
      const response = await axios.get('/api/gradebook/student/me/summary');
      return response.data;
    }
  });

  const getRiskBadge = (level: string) => {
    switch (level) {
      case 'red':
        return (
          <Badge variant="destructive" className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            En Riesgo
          </Badge>
        );
      case 'yellow':
        return (
          <Badge variant="secondary" className="flex items-center gap-1 bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            Atención
          </Badge>
        );
      case 'green':
        return (
          <Badge variant="default" className="flex items-center gap-1 bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Bien
          </Badge>
        );
      default:
        return null;
    }
  };

  const getScoreColor = (pct: number | null) => {
    if (pct === null) return 'text-gray-400';
    if (pct < 60) return 'text-red-600';
    if (pct < 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <BookOpen className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mis Calificaciones</h1>
          <p className="text-gray-500">Consulta tu avance académico por materia</p>
        </div>
      </div>

      {data?.summaries && data.summaries.length > 0 ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {data.summaries.map((summary) => (
            <Card key={summary.planId} className="overflow-hidden">
              <CardHeader className="pb-3 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{summary.courseName}</CardTitle>
                    <CardDescription>{summary.periodName}</CardDescription>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${getScoreColor(summary.finalAverage)}`}>
                      {summary.finalAverage !== null ? `${summary.finalAverage.toFixed(1)}%` : '--'}
                    </div>
                    {getRiskBadge(summary.riskLevel)}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {summary.categories.map((category) => {
                    const catKey = `${summary.planId}-${category.categoryId}`;
                    const isExpanded = expandedCategories.has(catKey);
                    return (
                      <div key={category.categoryId} className="border rounded-lg">
                        <button
                          type="button"
                          onClick={() => toggleCategory(catKey)}
                          className="w-full flex items-center justify-between px-3 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="font-medium">{category.categoryName}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">{category.weightPercent}%</span>
                            <span className={`font-bold ${getScoreColor(category.average)}`}>
                              {category.average !== null ? `${category.average.toFixed(1)}%` : '--'}
                            </span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-2">
                            {category.slots.map((slot) => (
                              <div 
                                key={slot.slotId} 
                                className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                              >
                                <span className="text-sm text-gray-700">{slot.title}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-500">
                                    {slot.rawScore ?? '-'}/{slot.maxScore}
                                  </span>
                                  <span className={`font-medium text-sm ${getScoreColor(slot.percentage)}`}>
                                    {slot.percentage !== null ? `${slot.percentage.toFixed(0)}%` : '--'}
                                  </span>
                                </div>
                              </div>
                            ))}
                            {category.slots.length === 0 && (
                              <p className="text-sm text-gray-400 italic py-2">
                                No hay actividades registradas
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              Sin calificaciones disponibles
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              Aún no hay calificaciones registradas en el sistema para tus materias actuales.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="bg-white p-3 rounded-full">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Indicadores de rendimiento
              </h3>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span className="text-gray-600">70% o más: Buen rendimiento</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                  <span className="text-gray-600">60-70%: Requiere atención</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span className="text-gray-600">Menos de 60%: En riesgo</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
